import { Col, ConfigProvider, Flex, Row } from 'antd';
import { useEffect, useState } from 'react';

import AuthMarketingBanner from '@/components/auth/AuthMarketingBanner';
import { usePagePadding } from '@/hooks/usePagePadding';

import { AUTH_LIGHT_THEME, authPanelMotionStyle } from './authLayout';

import type { ReactNode } from 'react';

type AuthPageShellProps = {
  bannerTitle: string;
  bannerClickable?: boolean;
  bannerSize?: 'default' | 'small';
  children: ReactNode;
};

const AuthPageShell = ({
  bannerTitle,
  bannerClickable = true,
  bannerSize = 'default',
  children,
}: AuthPageShellProps) => {
  const padding = usePagePadding();
  const [entered, setEntered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(true);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotionPreference = () => {
      setPrefersReducedMotion(media.matches);
      if (media.matches) {
        setEntered(true);
        return;
      }
      requestAnimationFrame(() => setEntered(true));
    };

    syncMotionPreference();
    media.addEventListener('change', syncMotionPreference);
    return () => media.removeEventListener('change', syncMotionPreference);
  }, []);

  return (
    <ConfigProvider theme={AUTH_LIGHT_THEME}>
      <Row
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'var(--ant-color-bg-container)',
          colorScheme: 'light',
        }}
      >
        <Col xs={24} md={bannerSize === 'small' ? 16 : 12}>
          <AuthMarketingBanner
            title={bannerTitle}
            clickable={bannerClickable}
            entered={entered}
            prefersReducedMotion={prefersReducedMotion}
          />
        </Col>
        <Col xs={24} md={bannerSize === 'small' ? 8 : 12}>
          <Flex
            vertical
            justify="space-between"
            style={{
              background: 'var(--ant-color-bg-container)',
              position: 'relative',
              height: '100dvh',
              overflowY: 'auto',
              ...padding,
              ...authPanelMotionStyle(entered, prefersReducedMotion),
            }}
          >
            {children}
          </Flex>
        </Col>
      </Row>
    </ConfigProvider>
  );
};

export default AuthPageShell;
